import { SIBLING_TYPE } from './config.js';

export class PedigreeSiblingGroup {
  constructor(family) {
    this.family = family;
    this.name = '';
    this.peopleInGroup = [];
    this.subGroups = [];
    this.depthInGeneration = -1;
    this.isEngagedInDepthCalculation = false;

    family.siblingGroups.push(this);
  }

  addPersonToGroup(person) {
    if (!this.peopleInGroup.includes(person)) {
      this.peopleInGroup.push(person);
      person.belongsToSiblingGroup = this;
    }
  }

  get leftMostSibling() {
    if (this.peopleInGroup.length === 0) return null;
    return this.peopleInGroup.sort((a, b) => a.virtualX - b.virtualX)[0];
  }

  get rightMostSibling() {
    if (this.peopleInGroup.length === 0) return null;
    const sorted = this.peopleInGroup.sort((a, b) => a.virtualX - b.virtualX);
    return sorted[sorted.length - 1];
  }

  get leftParent() {
    if (this.peopleInGroup.length === 0) return null;
    return this.peopleInGroup[0].leftParent;
  }

  get rightParent() {
    if (this.peopleInGroup.length === 0) return null;
    return this.peopleInGroup[0].rightParent;
  }

  get generationOfSiblings() {
    if (this.peopleInGroup.length === 0) return null;
    return this.peopleInGroup[0].rowDepth;
  }

  get depthInCurrentGeneration() {
    if (this.depthInGeneration === -1) {
      this.calculateDepthInCurrentGeneration();
    }
    return this.depthInGeneration;
  }

  calculateDepthInCurrentGeneration() {
    // Prevent infinite recursion
    if (this.isEngagedInDepthCalculation) return;

    if (this.peopleInGroup.length === 0) {
      this.depthInGeneration = 0;
      return;
    }

    this.isEngagedInDepthCalculation = true;

    // Get all other sibling groups in same generation
    const otherGroupsInGeneration = this.family.siblingGroups.filter(
      (sg) =>
        sg.generationOfSiblings === this.generationOfSiblings && sg !== this
    );

    // Find groups that overlap with this group
    const groupsIntersectingFromLeft = [];
    const groupsIntersectingFromRight = [];
    const groupsCoveringThisGroup = [];

    otherGroupsInGeneration.forEach((group) => {
      const thisLeft = this.leftMostSibling.virtualX;
      const thisRight = this.rightMostSibling.virtualX;
      const otherLeft = group.leftMostSibling.virtualX;
      const otherRight = group.rightMostSibling.virtualX;

      // Group intersects from left
      if (
        otherLeft < thisLeft &&
        otherRight > thisLeft &&
        otherRight < thisRight
      ) {
        groupsIntersectingFromLeft.push(group);
      }
      // Group intersects from right
      else if (
        otherRight > thisRight &&
        otherLeft < thisRight &&
        otherLeft > thisLeft
      ) {
        groupsIntersectingFromRight.push(group);
      }
      // Group covers this group
      else if (otherLeft < thisLeft && otherRight > thisRight) {
        groupsCoveringThisGroup.push(group);
      }
    });

    const allIntersecting = [
      ...groupsCoveringThisGroup,
      ...groupsIntersectingFromLeft,
      ...groupsIntersectingFromRight
    ];

    // If no overlaps, depth is 0 (on top)
    if (allIntersecting.length === 0) {
      this.depthInGeneration = 0;
    } else {
      const depths = allIntersecting.map((g) => g.depthInCurrentGeneration);
      const maxDepth = Math.max(...depths);

      if (maxDepth === -1) {
        this.depthInGeneration = 0;
      } else {
        this.depthInGeneration = maxDepth + 1;
      }
    }

    this.isEngagedInDepthCalculation = false;
  }

  resetDepthInGeneration() {
    this.depthInGeneration = -1;
  }

  get orderedListOfSiblings() {
    return this.getOrderedListOptimizedForAdditionalSpouses();
  }

  get orderedListOfSiblingsBasedOnVirtualX() {
    return [...this.peopleInGroup].sort((a, b) => a.virtualX - b.virtualX);
  }

  getOrderedListOptimizedForAdditionalSpouses() {
    let orderedList = [...this.peopleInGroup].sort((a, b) => a.pid - b.pid);

    const siblingToKeepLeft = this.getCandidateForLeftMostSibling(orderedList);
    const siblingToKeepRight =
      this.getCandidateForRightMostSibling(orderedList);

    // Move left candidate to beginning
    if (siblingToKeepLeft) {
      orderedList = orderedList.filter((s) => s !== siblingToKeepLeft);
      orderedList.unshift(siblingToKeepLeft);
    }

    // Move right candidate to end
    if (siblingToKeepRight) {
      orderedList = orderedList.filter((s) => s !== siblingToKeepRight);
      orderedList.push(siblingToKeepRight);
    }

    return orderedList;
  }

  getCandidateForLeftMostSibling(orderedList) {
    // Get eligible siblings with spouses on their right
    const eligibleSiblings = orderedList.filter((sibling) => {
      if (sibling.spouses.length === 0) return false;
      if (sibling.virtualX < sibling.spouses[0].virtualX) return false;
      return true;
    });

    // Priority 1: Spouse has parents AND siblings
    for (const sibling of eligibleSiblings) {
      for (const spouse of sibling.spouses) {
        if ((spouse.mother || spouse.father) && spouse.siblings.length > 0) {
          return sibling;
        }
      }
    }

    // Priority 2: Spouse has parents
    for (const sibling of eligibleSiblings) {
      for (const spouse of sibling.spouses) {
        if (spouse.mother || spouse.father) {
          return sibling;
        }
      }
    }

    // Priority 3: Spouse has siblings
    for (const sibling of eligibleSiblings) {
      for (const spouse of sibling.spouses) {
        if (spouse.siblings.length > 0) {
          return sibling;
        }
      }
    }

    return null;
  }

  /**
   * Find best sibling to place on right edge
   */
  getCandidateForRightMostSibling(orderedList) {
    // Get eligible siblings with spouses on their left
    const eligibleSiblings = orderedList.filter((sibling) => {
      if (sibling.spouses.length === 0) return false;
      if (sibling.spouses[0].virtualX < sibling.virtualX) return false;
      return true;
    });

    // Same priority logic as left candidate
    for (const sibling of eligibleSiblings) {
      for (const spouse of sibling.spouses) {
        if ((spouse.mother || spouse.father) && spouse.siblings.length > 0) {
          return sibling;
        }
      }
    }

    for (const sibling of eligibleSiblings) {
      for (const spouse of sibling.spouses) {
        if (spouse.mother || spouse.father) {
          return sibling;
        }
      }
    }

    for (const sibling of eligibleSiblings) {
      for (const spouse of sibling.spouses) {
        if (spouse.siblings.length > 0) {
          return sibling;
        }
      }
    }

    return null;
  }

  get subGroupOfNormalSiblings() {
    return (
      this.subGroups.find((sg) => sg.groupType === SIBLING_TYPE.normal) || null
    );
  }

  getPersonsSiblingSubGroup(person) {
    return this.subGroups.find((sg) => sg.peopleInGroup.has(person)) || null;
  }
}

export class PedigreeSiblingSubGroup {
  constructor(siblingGroup, groupType = SIBLING_TYPE.normal) {
    this.siblingGroup = siblingGroup;
    this.groupType = groupType; // 'normal', 'twin', 'triplet', etc.
    this.peopleInGroup = new Map(); // Map<Person, SiblingType>
  }

  static createNewSubGroup(siblingGroup) {
    const subGroup = new PedigreeSiblingSubGroup(siblingGroup);
    siblingGroup.subGroups.push(subGroup);
    return subGroup;
  }
}
